import { PlayerRefunds } from '../components/Refunds.tsx';
import { applyFilters, type OpsPlayer } from '@urbanrats/ops-core';
import { DataTable } from '../components/DataTable.tsx';
import { Link } from '../components/Link.tsx';
import { PageHead, Section, StateTag } from '../components/common.tsx';
import { fmtMoney, fmtTime, hrefWith, useCtx } from '../lib.ts';

export function Players() {
  const { data, filters } = useCtx();
  const f = applyFilters(data, filters);
  const betCount = (p: OpsPlayer) => f.bets.filter((b) => b.playerRef === p.playerRef).length;
  return (
    <>
      <PageHead title="Players">Pseudonymous references only (hash of operator + external id). No names, e-mails, IPs or documents are stored.</PageHead>
      <DataTable<OpsPlayer>
        testId="players-table"
        rows={f.players}
        rowKey={(p) => p.playerRef}
        empty="No players match these filters."
        exportName="players"
        columns={[
          { key: 'ref', header: 'Player', cell: (p) => <Link to={hrefWith(`/players/${p.playerRef}`, filters)}>{p.playerRef}</Link>, csv: (p) => p.playerRef },
          { key: 'op', header: 'Operator / brand', cell: (p) => `${p.operatorId} / ${p.brandId}`, csv: (p) => `${p.operatorId}/${p.brandId}` },
          { key: 'bal', header: 'Demo balance', numeric: true, cell: (p) => fmtMoney(p.balance), csv: (p) => fmtMoney(p.balance) },
          { key: 'bets', header: 'Bets', numeric: true, cell: betCount, csv: betCount },
          { key: 'since', header: 'Session since', cell: (p) => fmtTime(p.startedAt), csv: (p) => p.startedAt },
        ]}
      />
    </>
  );
}

export function PlayerDetail({ id }: { id: string }) {
  const { data, filters } = useCtx();
  const p = data.players.find((x) => x.playerRef === id);
  if (!p) return <p className="empty">Player not found in your scope.</p>;
  const rewards = data.rewards.filter((r) => r.playerRef === id);
  return (
    <>
      <PageHead title={`Player ${p.playerRef}`}>
        {p.operatorId} / {p.brandId} · session {p.sessionId} · balance {fmtMoney(p.balance)} (demo wallet)
      </PageHead>
      <p>
        <Link to={hrefWith('/bets', filters, { player: id })}>Bets of this player →</Link>
      </p>
      <PlayerRefunds playerRef={p.playerRef} />
      <Section title="Rewards (not money)" testId="player-rewards">
        {rewards.length === 0 ? (
          <p className="muted">No rewards.</p>
        ) : (
          <ul className="plain">
            {rewards.map((r) => (
              <li key={r.item.promotionId}>
                {r.item.kind} {r.item.faceValue ? fmtMoney(r.item.faceValue) : `+${(r.item.boostBps ?? 0) / 100}%`} <StateTag state={r.item.state} /> <span className="muted">{r.item.origin}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
}
